import { useEffect, useState } from 'react';
import { Database, AlertCircle, RefreshCw, HardDrive, Cpu } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { portalGovernanceApi, type UsageOverview } from '../../api/portalGovernance';

function fmtBytes(n: number | null | undefined): string {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n; let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function UsageApp() {
  const [data, setData] = useState<UsageOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try { setData(await portalGovernanceApi.usage()); }
    catch (err: any) { setError(err?.response?.data?.detail || 'Could not load usage.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  if (loading) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;

  const t = data?.totals;

  return (
    <div className="space-y-6">
      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Database size={18} className="text-slate-500" />
          <div><h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Usage Tracking</h2>
          <p className="text-sm text-slate-500">How much each user is using across the platform.</p></div>
        </div>
        <Button size="sm" variant="secondary" onClick={() => load()} disabled={loading}><RefreshCw size={14} /></Button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card className="p-3"><div className="flex items-center gap-2 text-slate-500"><HardDrive size={14} />Storage</div><div className="mt-1 text-lg font-bold text-slate-950 dark:text-slate-100">{fmtBytes(t?.storage_used_bytes ?? 0)}</div><div className="text-xs text-slate-500">of {fmtBytes(t?.storage_quota_bytes ?? 0)}</div></Card>
        <Card className="p-3"><div className="flex items-center gap-2 text-slate-500"><Cpu size={14} />AI tokens</div><div className="mt-1 text-lg font-bold text-slate-950 dark:text-slate-100">{(t?.ai_tokens ?? 0).toLocaleString()}</div><div className="text-xs text-slate-500">all users</div></Card>
        <Card className="p-3"><div className="text-xs text-slate-500">AI cost</div><div className="mt-1 text-lg font-bold text-slate-950 dark:text-slate-100">${(t?.ai_cost_usd ?? 0).toFixed(2)}</div><div className="text-xs text-slate-500">recorded</div></Card>
        <Card className="p-3"><div className="text-xs text-slate-500">Users</div><div className="mt-1 text-lg font-bold text-slate-950 dark:text-slate-100">{t?.users ?? 0}</div><div className="text-xs text-slate-500">tracked</div></Card>
      </div>

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 dark:border-slate-700">
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Storage</th>
                <th className="px-4 py-3 font-medium">AI tokens</th>
                <th className="px-4 py-3 font-medium">Calls</th>
                <th className="px-4 py-3 font-medium">Tickets</th>
              </tr>
            </thead>
            <tbody>
              {(data?.users.length ?? 0) === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No users.</td></tr>}
              {data?.users.map(u => (
                <tr key={u.user_id} className="border-b border-slate-100 last:border-0 dark:border-slate-800">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-950 dark:text-slate-100">{u.username || '—'}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs capitalize text-slate-600 dark:bg-slate-800 dark:text-slate-300">{u.role || '—'}</span></td>
                  <td className="px-4 py-3">
                    <div className="text-slate-950 dark:text-slate-100">{fmtBytes(u.storage.used_bytes)}</div>
                    <div className="text-xs text-slate-500">{u.storage.usage_percent == null ? '—' : `${u.storage.usage_percent}%`} of {fmtBytes(u.storage.quota_bytes)}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-950 dark:text-slate-100">{u.ai.total_tokens.toLocaleString()}</td>
                  <td className="px-4 py-3 text-slate-500">{u.ai.calls}</td>
                  <td className="px-4 py-3 text-slate-500">{u.support.tickets}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
